import type { FileWithPath } from '@mantine/dropzone'
import { notifications } from '@mantine/notifications'
import { useRef, useState } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'
import type { PrepareUploadResponse } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'

type UseFileUploadArgs = {
  onPrepareError?: () => void
  onUploadError?: () => void
  onUploadSuccess?: () => void
  onUploadCanceled?: () => void
}

const FileUpdloadExtensions = {
  text: /\.txt$/,
  markdown: /\.md$/,
  pdf: /\.pdf$/,
  doc: /\.(doc|docx)$/,
  powerpoint: /\.(ppt|pptx)$/,
  image: /\.(png|jpg|jpeg|gif|webp|svg)$/i,
  video: /\.(mp4|avi|mov|wmv|flv|webm)$/i,
} as const

const detectFileType = (file: FileWithPath) => {
  const matches = Object.entries(FileUpdloadExtensions).find(([_, pattern]) => file.path?.match(pattern))
  return matches ? matches[0] : null
}

export function useFileUpload(args?: UseFileUploadArgs): {
  uploading: boolean
  uploadProgress: number
  upload: (file: FileWithPath) => Promise<PrepareUploadResponse | undefined>
  cancel: () => void
} {
  const { api } = useGlobalState()

  const { onPrepareError, onUploadError, onUploadSuccess, onUploadCanceled } = args ?? {}

  const [uploading, setUploading] = useState<boolean>(false)
  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const abortControllerRef = useRef<AbortController | null>(null)

  const cancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      console.log('[useFileUpload] Upload canceled by user')
    }
  }

  async function upload(file: FileWithPath) {
    const fileType = detectFileType(file)
    if (fileType) {
      console.log(`useFileUpload: Uploading ${fileType} file - ${file.path}`)
    } else {
      console.log(`useFileUpload: Uploading unknown file type - ${file.path}`)
    }

    setUploading(true)
    setUploadProgress(0)

    // Create a new AbortController for this upload
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    try {
      const prepareResponse = await api.safeRequest(api.uploadPrepare, { fileType: file.type }, abortController.signal)
      console.log('[useFileUpload] prepareResponse', prepareResponse)

      // Check if canceled after first async operation
      if (abortController.signal.aborted) {
        console.log('[useFileUpload] Upload canceled after prepare')
        onUploadCanceled?.()
        setUploading(false)
        return
      }

      const uploadProblemMessage = 'There was an issue preparing to the file. Please try again or contact support.'

      if (!prepareResponse.ok) {
        notifications.show({
          title: 'Something went wrong.',
          message: uploadProblemMessage,
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
        onPrepareError?.()
        setUploading(false)
        return
      }

      // Check if canceled before second async operation
      if (abortController.signal.aborted) {
        console.log('[useFileUpload] Upload canceled before upload')
        onUploadCanceled?.()
        setUploading(false)
        return
      }

      const uploadResponse = await api.safeRequest(
        api.upload,
        {
          file_path: prepareResponse.data.filePath,
          file_content: file,
        },
        abortController.signal,
        setUploadProgress,
      )

      console.log('[useFileUpload] uploadResponse', uploadResponse)

      // Check if canceled after second async operation
      if (abortController.signal.aborted) {
        console.log('[useFileUpload] Upload canceled after upload')
        onUploadCanceled?.()
        setUploading(false)
        return
      }

      if (!uploadResponse.ok) {
        notifications.show({
          title: 'Something went wrong.',
          message: uploadProblemMessage,
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
        onUploadError?.()
        setUploading(false)
        return
      }

      onUploadSuccess?.()
      setUploading(false)
      return prepareResponse.data
    } catch (error) {
      // Handle any errors, including potential abort errors
      if (abortController.signal.aborted || (error as any)?.name === 'AbortError') {
        console.log('[useFileUpload] Upload canceled due to abort')
        onUploadCanceled?.()
      } else {
        console.error('[useFileUpload] Upload error:', error)
        notifications.show({
          title: 'Something went wrong.',
          message: 'There was an issue uploading the file. Please try again or contact support.',
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
        onUploadError?.()
      }
      setUploading(false)
      return
    } finally {
      // Clean up the abort controller reference
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null
      }
    }
  }

  return { upload, uploading, uploadProgress, cancel }
}
